import { roundToPlaces } from "../../utils/roundToPlaces.js";
import type { CountrySettings } from "../CountrySettings.js";
import type { Customer } from "../Customer.js";
import type { EconomicSubjectVatType } from "../EconomicSubjectVatType.js";
import type { ExchangeRate } from "../ExchangeRatesSetting.js";
import type { OrderInput } from "../Order.js";
import type { OrderPricesInput } from "../OrderPrices.js";
import type { Subcategory } from "../Subcategory.js";
import type { TaxedPrice } from "../TaxedPrice.js";
import { calculateOrderDiscounts } from "./calculateOrderDiscounts.js";
import { calculatePriceOfDelivery } from "./calculatePriceOfDelivery.js";
import { calculateRoundingAdjustment } from "./calculateRoundingAdjustment.js";
import { getPriceOfOrderProduct } from "./getPriceOfOrderProduct.js";
import { getWeightInGramsOfOrder } from "./getWeightInGramsOfOrder.js";
import { shouldApplyTax } from "./shouldApplyTax.js";

interface OrderPriceSettings {
	countrySettings: CountrySettings[];
	customer: Customer | null;
	exchangeRates: ExchangeRate[];
	subcategories: Pick<Subcategory, "id" | "eligibleForB2cDiscount">[];
	vatType: EconomicSubjectVatType;
}

export function calculatePriceOfOrder(
	order: Pick<OrderInput, "products" | "billingAddress" | "deliveryAddress" | "payment" | "deliveryType">,
	{ countrySettings, customer, exchangeRates, subcategories, vatType }: OrderPriceSettings,
): OrderPricesInput {
	let withTax = 0;
	let withoutTax = 0;

	const applyTax = shouldApplyTax(order.deliveryAddress.country, vatType);

	for (const product of order.products) {
		const { withTax: productWithTax, withoutTax: productWithoutTax } = getPriceOfOrderProduct(product, { applyTax });
		withTax += productWithTax;
		withoutTax += productWithoutTax;
	}

	let deliveryPrice: TaxedPrice = {
		taxRate: 0,
		withTax: 0,
		withoutTax: 0,
	};
	if (order.products.length > 0) {
		deliveryPrice = calculatePriceOfDelivery(order.deliveryAddress.country, order.deliveryType, {
			countrySettings,
			customer,
			weightInGrams: getWeightInGramsOfOrder(order),
			exchangeRates,
			currency: order.payment.currency,
			applyTax,
		});
	}

	const { freeDelivery, discountsByTaxRate } = calculateOrderDiscounts(order, {
		customer,
		countrySettings,
		subcategories,
	});

	const productDiscountWithTax = discountsByTaxRate.reduce((sum, { withTax }) => sum + withTax, 0);
	const productDiscountWithoutTax = discountsByTaxRate.reduce((sum, { withoutTax }) => sum + withoutTax, 0);
	const deliveryDiscountWithTax = freeDelivery ? deliveryPrice.withTax : 0;
	const deliveryDiscountWithoutTax = freeDelivery ? deliveryPrice.withoutTax : 0;

	withTax -= productDiscountWithTax;
	withoutTax -= productDiscountWithoutTax;
	deliveryPrice.withTax -= deliveryDiscountWithTax;
	deliveryPrice.withoutTax -= deliveryDiscountWithoutTax;
	withTax += deliveryPrice.withTax;
	withoutTax += deliveryPrice.withoutTax;

	if (!applyTax) {
		const totalWithoutTax = roundToPlaces(withoutTax, 2);
		const roundingAdjustment = calculateRoundingAdjustment(totalWithoutTax, order.payment.currency);
		return {
			// `calculatePriceOfDelivery` already accounts for "applyTax"
			deliveryPrice,
			roundingAdjustment,
			totalPrice: {
				// This is intentional. There is no VAT, so it does not matter.
				withoutTax: totalWithoutTax + roundingAdjustment,
				withTax: totalWithoutTax + roundingAdjustment,
			},
			appliedDiscounts: {
				deliveryPrice: deliveryDiscountWithoutTax,
				discountsByTaxRate,
			},
		};
	}

	const totalWithTax = roundToPlaces(withTax, 2);
	const roundingAdjustment = calculateRoundingAdjustment(totalWithTax, order.payment.currency);

	return {
		deliveryPrice,
		roundingAdjustment,
		totalPrice: {
			withoutTax: roundToPlaces(withoutTax, 2),
			withTax: totalWithTax + roundingAdjustment,
		},
		appliedDiscounts: {
			deliveryPrice: deliveryDiscountWithTax,
			discountsByTaxRate,
		},
	};
}
